'use client';

import * as Popover from '@radix-ui/react-popover';
import React, { memo, useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Add } from '@crabs-ai/web-icons';
import {
  Button,
  Segmented,
  SegmentedContainer,
  SegmentedItem,
} from '@crabs-ai/ds-react';
import type {
  AnimationCategory,
  AnimationType,
} from '../../items/animation/animation-item-type';
import AnimationItem from '../../editor-property-panel/ui/contents/animation/AnimationItem';
import { getPresetsByCategory } from '../../editor-property-panel/ui/contents/animation/animation-presets';
import { addAnimation } from '../../state/actions/add-animation';
import { useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';

const ANIMATION_CATEGORIES: AnimationCategory[] = ['in', 'out', 'combo'];

/**
 * Inspector 에서 애니를 추가하는 팝오버.
 *
 * 좌측 패널 → 타임라인 드롭이 기존 경로지만, 그룹 멤버처럼 타임라인에 행이 없는 아이템은
 * 드롭 타깃이 없다. 클릭 추가를 Inspector 에 두어 모든 아이템이 같은 경로를 쓴다
 * (드래그&드롭은 그대로 유지).
 */
const AnimationAddPopoverUnmemoized: React.FC<{ itemId: string }> = ({
  itemId,
}) => {
  const t = useTranslations();
  const timelineWriteContext = useWriteContext();
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState<AnimationCategory>('in');
  // 다중 선택이면 선택 전체에 일괄 추가한다 (값·시간·삭제 편집과 동일한 규칙).
  const targetIds = useTargetItemIds(itemId);

  const handleAdd = useCallback(
    (animationType: AnimationType) => {
      addAnimation({
        timelineWriteContext,
        targetItemId: itemId,
        targetItemIds: targetIds,
        animationType,
        // 이미 선택된 아이템이라 선택을 갈아끼울 필요가 없다(그룹 멤버 depth 도 유지된다).
        preserveSelection: true,
      });
      setIsOpen(false);
    },
    [timelineWriteContext, itemId, targetIds],
  );

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Popover.Trigger asChild>
        <Button
          hierarchy="tertiary"
          size="small"
          className="w-full"
          leadingNode={<Add className="size-4" />}
        >
          {t('Editor.inspector.animation.add')}
        </Button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="left"
          align="start"
          sideOffset={8}
          className="z-50 flex w-[320px] flex-col gap-2xs rounded-xs border border-sys-style-neutral-light bg-sys-bg-surface p-2xs inset-shadow-basic-md"
        >
          <Segmented
            value={category}
            onValueChange={(value) => setCategory(value as AnimationCategory)}
            size="sm"
          >
            <SegmentedContainer>
              {ANIMATION_CATEGORIES.map((animationCategory) => (
                <SegmentedItem
                  key={animationCategory}
                  value={animationCategory}
                >
                  {t(`Editor.animation.category.${animationCategory}`)}
                </SegmentedItem>
              ))}
            </SegmentedContainer>
          </Segmented>

          <div className="grid max-h-[320px] grid-cols-2 gap-2xs overflow-y-auto">
            {getPresetsByCategory(category).map((preset) => (
              <AnimationItem
                key={preset.type}
                type={preset.type}
                name={t(`Editor.animation.type.${preset.type}`)}
                onSelect={handleAdd}
              />
            ))}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export const AnimationAddPopover = memo(AnimationAddPopoverUnmemoized);
